'use client'

import { useState, useRef, useCallback } from 'react'
import {
  Bookmark,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Share2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'

type Asset = {
  type: string
  url: string | null
  thumbnailUrl: string | null
  title: string | null
}

type InstagramPostCardProps = {
  imageUrl: string | null
  caption: string
  hashtags: string[]
  username: string
  images?: string[]
  assets?: Asset[]
}

function Carousel({
  assets,
  caption,
  images,
}: {
  assets: Asset[]
  caption: string
  images?: string[]
}) {
  const [slide, setSlide] = useState(0)
  const total = assets.length
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)

  const goNext = useCallback(() => {
    setSlide((s) => Math.min(s + 1, total - 1))
  }, [total])

  const goPrev = useCallback(() => {
    setSlide((s) => Math.max(s - 1, 0))
  }, [])

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true)
    setStartX(e.clientX)
    setDragOffset(0)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return
    setDragOffset(e.clientX - startX)
  }

  const handlePointerUp = () => {
    if (!isDragging) return
    setIsDragging(false)
    if (dragOffset < -50 && slide < total - 1) {
      goNext()
    } else if (dragOffset > 50 && slide > 0) {
      goPrev()
    }
    setDragOffset(0)
  }

  if (total === 1) {
    const asset = assets[0]
    const displayUrl =
      asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
    return (
      <div className="w-full bg-input/40">
        {asset.type === 'VIDEO' ? (
          <video
            src={asset.url ?? undefined}
            controls
            playsInline
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <img
            src={displayUrl ?? undefined}
            alt={caption.slice(0, 80)}
            className="max-w-full max-h-full object-contain"
          />
        )}
      </div>
    )
  }

  const translateX = -(slide * 100) + dragOffset

  return (
    <div className="relative w-full bg-input/40 overflow-hidden">
      <div
        ref={containerRef}
        className="flex select-none"
        style={{ transform: `translateX(${translateX}%)` }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {assets.map((asset) => {
          const displayUrl =
            asset.type === 'VIDEO' ? asset.thumbnailUrl : asset.url
          return (
            <div
              key={asset.url ?? asset.title ?? ''}
              className="w-full shrink-0"
            >
              {asset.type === 'VIDEO' ? (
                <video
                  src={asset.url ?? undefined}
                  controls
                  playsInline
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <img
                  src={displayUrl ?? undefined}
                  alt={caption.slice(0, 80)}
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>
          )
        })}
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            disabled={slide === 0}
            className="absolute left-2 top-1/2 -translate-y-1/2 size-7 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-0 hover:bg-black/60 transition-colors"
            aria-label="Previous asset"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={slide >= total - 1}
            className="absolute right-2 top-1/2 -translate-y-1/2 size-7 rounded-full bg-black/40 text-white flex items-center justify-center disabled:opacity-0 hover:bg-black/60 transition-colors"
            aria-label="Next asset"
          >
            <ChevronRight className="size-4" />
          </button>
        </>
      )}

      {total > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
          {assets.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setSlide(i)}
              className={`size-1.5 rounded-full transition-all ${
                i === slide ? 'bg-white w-4' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function InstagramPostCard({
  imageUrl,
  caption,
  hashtags,
  username,
  images,
  assets,
}: InstagramPostCardProps) {
  const t = useTranslations('InstagramPostCard')

  const carouselAssets =
    assets && assets.length > 0
      ? assets.filter((a) => a.url || a.thumbnailUrl)
      : []

  const imageAssets = images && images.length > 0
    ? images.map((url) => ({ type: 'IMAGE', url, thumbnailUrl: null, title: null }))
    : []

  const displayAssets =
    imageAssets.length > 0 ? imageAssets :
    carouselAssets.length > 0 ? carouselAssets :
    [{ type: 'IMAGE', url: imageUrl, thumbnailUrl: null, title: null }]

  return (
    <div className="w-full mx-auto rounded-2xl bg-card shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <div
            className="size-8 shrink-0 rounded-full"
            style={{
              background:
                'linear-gradient(135deg, #f9a825 0%, #e91e8c 50%, #9c27b0 100%)',
            }}
            aria-hidden
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold leading-none">
              {username}
            </span>
            <span className="text-[11px] text-muted-foreground leading-none mt-0.5">
              {t('sponsoredLabel')}
            </span>
          </div>
        </div>
        <button
          type="button"
          className="flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted transition-colors"
          aria-label="More options"
        >
          <MoreHorizontal className="size-5" />
        </button>
      </div>

      {/* Media / Carousel */}
      <Carousel assets={displayAssets} caption={caption} />

      {/* Actions */}
      <div className="flex items-center px-3 pt-2.5 pb-1">
        <div className="flex items-center gap-3 flex-1">
          <button
            type="button"
            className="text-foreground hover:text-rose-500 transition-colors"
            aria-label="Like"
          >
            <Heart className="size-6" />
          </button>
          <button
            type="button"
            className="text-foreground hover:text-foreground/70 transition-colors"
            aria-label="Comment"
          >
            <MessageCircle className="size-6" />
          </button>
          <button
            type="button"
            className="text-foreground hover:text-foreground/70 transition-colors"
            aria-label="Share"
          >
            <Share2 className="size-6" />
          </button>
        </div>
        <button
          type="button"
          className="text-foreground hover:text-foreground/70 transition-colors"
          aria-label="Save"
        >
          <Bookmark className="size-6" />
        </button>
      </div>

      {/* Likes */}
      <div className="px-3 pb-1">
        <span className="text-sm font-semibold">1.234 {t('likesLabel')}</span>
      </div>

      {/* Caption */}
      <div className="px-3 pb-1">
        <p className="text-sm leading-snug">
          <span className="font-semibold mr-1">{username}</span>
          <span className="whitespace-pre-wrap">{caption}</span>
        </p>
      </div>

      {/* Hashtags */}
      {hashtags.length > 0 && (
        <div className="px-3 pb-1">
          <p className="text-sm leading-snug text-blue-500 dark:text-blue-400">
            {hashtags.map((tag, i) => (
              <span key={i}>
                {i > 0 && ' '}
                <span>{tag}</span>
              </span>
            ))}
          </p>
        </div>
      )}

      {/* Timestamp */}
      <div className="px-3 pb-3">
        <span className="text-[11px] text-muted-foreground uppercase tracking-wide">
          Vor 2 Stunden
        </span>
      </div>

      {/* Publish */}
      <div className="px-3 pb-4">
        <Button
          type="button"
          className="w-full"
          onClick={() => toast.info(t('comingSoon'))}
        >
          {t('publish')}
        </Button>
      </div>
    </div>
  )
}
